import { useResource } from '@landsbankinn/l16n';
import React, {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import {
  AppHubServiceEvents,
  AppHubServiceToken,
  type AppHubState,
  AppHubStateType,
  type AppHubUser,
  type IAppHubService,
} from 'src/services/AppHubService';
import { ServicesProvider } from 'src/services/ServicesProvider';
import {
  type IWindowLocationService,
  WindowLocationServiceToken,
} from 'src/services/WindowLocationService';
import {
  type LoginServerState,
  useApplicationClientContext,
  useApplicationServerContext,
} from 'src/state';
import { AllowedLoginType, isLoginServerState } from 'src/state/server';
import { res } from './AppContext.res';

type AppContextType = {
  state?: AppHubState;
  showQrCode: boolean;
  setShowQrCode: (showQrCode: boolean) => void;
  authenticateUser: (user: AppHubUser) => void;
  revokeUser: (user: AppHubUser) => void;
  fetchQrCode: () => void;
  setPersistanceOptIn: (isPersistent: any) => void;
  start: () => void;
};

const AppContext = React.createContext<AppContextType>({
  showQrCode: false,
  setShowQrCode: () => void 0,
  authenticateUser: () => void 0,
  revokeUser: () => void 0,
  fetchQrCode: () => void 0,
  setPersistanceOptIn: () => void 0,
  start: () => void 0,
});

export function useAppService() {
  return useContext(AppContext);
}

export function AppContextProvider(props: { children?: ReactNode }) {
  const { r } = useResource();
  const navigate = useNavigate();
  const [state, setState] = useState<AppHubState | undefined>(undefined);
  const [showQrCode, setShowQrCode] = useState(false);
  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const [, dispatch] = useApplicationClientContext();
  const windowLocationService =
    ServicesProvider.getSingleton<IWindowLocationService>(
      WindowLocationServiceToken,
      navigate,
    );

  const appHubService = useMemo(() => {
    // No need to start the AppHubService if App login type is not allowed
    // Using null checks here because serverState might not be of type LoginServerState
    if (
      isLoginServerState(serverState) &&
      serverState.allowedLoginTypes.includes(AllowedLoginType.App)
    ) {
      const appHubService =
        ServicesProvider.getSingleton<IAppHubService>(AppHubServiceToken);

      appHubService.start();

      return appHubService;
    }
  }, [serverState]);

  useEffect(() => {
    const listenerIds: string[] = [];
    if (appHubService) {
      listenerIds.push(
        appHubService.on(AppHubServiceEvents.OnStateChanged, setState),
      );
    }
    return () => {
      if (appHubService) {
        appHubService.off(listenerIds);
      }
    };
  }, [appHubService]);

  useEffect(() => {
    if (state) {
      switch (state?.type) {
        case AppHubStateType.Pending:
          dispatch({ type: 'setIsSubmitting' });
          break;
        case AppHubStateType.Completed:
          setTimeout(() => {
            // Allow for UI transitions/animations to complete before redirecting
            windowLocationService.goto(state.url, navigate);
          }, 1000);
          break;
        case AppHubStateType.Suspended:
          if (state.url) {
            windowLocationService.goto(state.url, navigate);
          } else {
            dispatch({
              type: 'setLoginErrorState',
              payload: { errorMessage: r(res.timeout), retryAction: 'Cancel' },
            });
          }
          break;
        case AppHubStateType.Error:
          dispatch({
            type: 'setLoginErrorState',
            payload: { errorMessage: r(res.error), retryAction: 'Cancel' },
          });
          break;
      }
    }
  }, [dispatch, r, state, windowLocationService, navigate]);

  const authenticateUser = useCallback(
    async (user: AppHubUser) => {
      return appHubService?.authenticateUser(user);
    },
    [appHubService],
  );

  const revokeUser = useCallback(
    async (user: AppHubUser) => {
      return appHubService?.revokeUser(user);
    },
    [appHubService],
  );

  const fetchQrCode = useCallback(async () => {
    return appHubService?.fetchQrCode();
  }, [appHubService]);

  const setPersistanceOptIn = useCallback(
    async (isPersistent: boolean) => {
      return appHubService?.setPersistent(isPersistent);
    },
    [appHubService],
  );

  const start = useCallback(async () => {
    return appHubService?.start();
  }, [appHubService]);

  const ctx = useMemo(() => {
    return {
      state,
      showQrCode,
      setShowQrCode: (showQrCode: boolean) => {
        setShowQrCode(showQrCode);
      },
      authenticateUser,
      revokeUser,
      fetchQrCode,
      setPersistanceOptIn,
      start,
    };
  }, [
    state,
    showQrCode,
    authenticateUser,
    fetchQrCode,
    revokeUser,
    setPersistanceOptIn,
    start,
  ]);

  return (
    <AppContext.Provider value={ctx}>{props.children}</AppContext.Provider>
  );
}
